import { PortfolioProject } from '@/data/portfolio-projects';
import { trackEvent } from '@/utils/analytics';
import React from 'react';

export interface ProjectCardProps {
    project: PortfolioProject;
    className?: string;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, className = '' }) => {
    return (
        <div
            className={`bg-vscode-bg-secondary border border-gray-600/30 rounded-lg overflow-hidden hover:border-vscode-accent/50 transition-all duration-300 group ${className}`}
        >
            {/* Project Image */}
            <div className='relative h-48 bg-vscode-bg overflow-hidden flex-shrink-0'>
                <img
                    src={project.image}
                    alt={project.title}
                    className='w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300'
                    loading='lazy'
                />
                <div className='absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-300' />
            </div>

            {/* Project Content */}
            <div className='p-6 flex flex-col flex-1'>
                <div className='flex-1'>
                    <h3 className='text-xl font-semibold text-vscode-accent mb-3 group-hover:text-vscode-accent/80 transition-colors'>
                        {project.title}
                    </h3>

                    <p className='text-vscode-text-muted text-sm leading-relaxed mb-4'>{project.description}</p>

                    {/* Technologies */}
                    {project.technologies && (
                        <div className='flex flex-wrap gap-2'>
                            {project.technologies.map((tech, index) => (
                                <span
                                    key={index}
                                    className='px-2 py-1 bg-vscode-bg text-vscode-text-muted text-xs rounded border border-gray-600/30'
                                >
                                    {tech}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Action Button */}
                {project.link ? (
                    <a
                        href={project.link}
                        target='_blank'
                        rel='noopener noreferrer'
                        onClick={() => {
                            trackEvent('external_link_clicked', {
                                url: project.link,
                                link_text: 'View Project',
                                project_title: project.title,
                            });
                        }}
                        className='w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded transition-colors duration-200 mt-6 text-center cursor-pointer inline-block'
                    >
                        View Project
                    </a>
                ) : (
                    <button
                        className='w-full px-4 py-2 bg-gray-600 text-gray-400 font-medium rounded mt-6 cursor-not-allowed'
                        disabled
                    >
                        Unavailable
                    </button>
                )}
            </div>
        </div>
    );
};
